// overrides/components/pusher/pusher-provider.tsx
'use client';

import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';

declare global {
  interface Window {
    Pusher: typeof Pusher;
  }
}

interface WebSocketConfig {
  key: string;
  host: string;
  port: number;
  authToken?: string;
}

const PusherContext = createContext<Echo | null>(null);

interface PusherProviderProps {
  children: ReactNode;
  websocketConfig: WebSocketConfig;
}

export const PusherProvider: React.FC<PusherProviderProps> = ({ children, websocketConfig }) => {
  const [echo, setEcho] = useState<Echo | null>(null);
  const isInitialized = useCustomerStore(state => state.isInitialized);
  const me = useCustomerStore(state => state.me);

  useEffect(() => {
    if (!isInitialized || !me) return;

    if (typeof window !== 'undefined') {
      window.Pusher = Pusher;
    }

    const echoInstance = new Echo({
      broadcaster: 'pusher',
      key: websocketConfig.key,
      wsHost: websocketConfig.host,
      wsPort: websocketConfig.port,
      wssPort: websocketConfig.port,
      forceTLS: false,
      encrypted: true,
      disableStats: true,
      enabledTransports: ['ws', 'wss'],
      authEndpoint: `${process.env.NEXT_PUBLIC_API_URL}/store/broadcasting/auth`,
      auth: {
        headers: {
          Authorization: `Bearer ${me.authToken}`,
          Accept: 'application/json',
        },
      },
    });

    setEcho(echoInstance);

    // DEBUG: Add connection status logging

    echoInstance.connector.pusher.connection.bind('connected', () => {
      if (process.env.NODE_ENV === 'development') console.log('🚀 PUSHER CONNECTED');
    });

    echoInstance.connector.pusher.connection.bind('disconnected', () => {
      if (process.env.NODE_ENV === 'development') console.log('❌ PUSHER DISCONNECTED');
    });

    echoInstance.connector.pusher.connection.bind('error', (error: any) => {
      if (process.env.NODE_ENV === 'development') console.log('💥 PUSHER ERROR:', error);
    });

    // DEBUG: Listen to ALL events on the customer channel
    const customerChannel = echoInstance.private('App.Models.Customer.' + me.customer.id);

    (customerChannel as any).listenToAll((eventName: string, eventData: any) => {
      if (process.env.NODE_ENV === 'development')
        console.log('🔥 CUSTOMER CHANNEL ALL EVENTS:', {
          eventName,
          eventData,
          customerId: me.customer.id,
        });
    });

    customerChannel.listen('.message-received', (e: any) => {
      if (process.env.NODE_ENV === 'development') console.log('✅ MESSAGE RECEIVED:', e);
    });

    return () => {
      echoInstance.disconnect();
      setEcho(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websocketConfig, me?.authToken, isInitialized]);

  return <PusherContext.Provider value={echo}>{children}</PusherContext.Provider>;
};

export const usePusher = (): Echo | null => {
  return useContext(PusherContext);
};
